/**
 * Algorithm Detail Route
 * View and edit individual algorithm
 */

import { createFileRoute, useNavigate, Link } from '@tanstack/react-router'
import { useState } from 'react'
import { useAppStore } from '@/lib/store'
import { AlgorithmForm } from '@/components/algorithms/algorithm-form'
import { PerformanceDashboard } from '@/components/performance/performance-dashboard'
import { TradingControls } from '@/components/trading/trading-controls'
import { Tabs, TabsPanel, TabPanel } from '@/components/trading/tabs'
import { ArrowLeft } from 'lucide-react'
import { Button } from '@/components/ui/button'

export const Route = createFileRoute('/dashboard/algorithms/$id')({
  component: AlgorithmDetail,
})

function AlgorithmDetail() {
  const { id } = Route.useParams()
  const navigate = useNavigate()
  const { state, actions } = useAppStore()
  const [activeTab, setActiveTab] = useState('overview')
  const [isEditing, setIsEditing] = useState(false)

  // Mock algorithm data
  const mockAlgorithm = {
    id: '1',
    name: 'Trend Following EUR/USD',
    description: 'Captures trends in EUR/USD using moving average crossovers and RSI filters',
    strategy: 'trend-following',
    symbol: 'EUR/USD',
    timeframe: '1h',
    status: 'active' as const,
    parameters: {
      entryConditions: ['MA crossover', 'RSI < 70', 'Volume confirmation'],
      exitConditions: ['Reverse crossover', 'Stop loss hit', 'Take profit hit'],
      positionSize: 1,
      maxPositions: 3,
      stopLoss: 50,
      takeProfit: 100,
      trailingStop: 25,
    },
    riskManagement: {
      maxDrawdown: 20,
      dailyLossLimit: 500,
      maxRiskPerTrade: 2,
      emergencyStopEnabled: true,
    },
    schedule: {
      enabled: true,
      startTime: '08:00',
      endTime: '17:00',
      weekdays: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'],
    },
    performance: {
      totalReturn: 1250,
      totalReturnPercent: 12.5,
      winRate: 65,
      profitFactor: 2.1,
      sharpeRatio: 1.8,
      maxDrawdown: 8.5,
      totalTrades: 45,
      avgWin: 85,
      avgLoss: 45,
    },
    createdAt: '2024-01-15',
  }

  const algorithm = state.algorithms.find((a) => a.id === id) || mockAlgorithm

  const handleSave = (updatedAlgorithm: any) => {
    actions.updateAlgorithm(id, updatedAlgorithm)
    setIsEditing(false)
  }

  const handleCancel = () => {
    setIsEditing(false)
  }

  const handleStart = () => {
    actions.updateAlgorithm(id, { status: 'active' })
  }

  const handleStop = () => {
    actions.updateAlgorithm(id, { status: 'inactive' })
  }

  const handleEmergencyStop = () => {
    actions.updateAlgorithm(id, { status: 'inactive' })
    // Additional emergency logic would go here
  }

  return (
    <div className="p-8 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link to="/dashboard/algorithms">
          <Button variant="ghost" size="sm">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Algorithms
          </Button>
        </Link>
      </div>

      {/* Tabs */}
      <Tabs
        tabs={[
          { id: 'overview', label: 'Overview' },
          { id: 'performance', label: 'Performance' },
          { id: 'settings', label: 'Settings' },
        ]}
        defaultTab="overview"
        onChange={setActiveTab}
        variant="pills"
      />

      <TabsPanel>
        <TabPanel value="overview" activeTab={activeTab}>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-6">
              {isEditing ? (
                <AlgorithmForm
                  algorithm={algorithm}
                  onSave={handleSave}
                  onCancel={handleCancel}
                />
              ) : (
                <>
                  {/* Algorithm Info */}
                  <div className="p-6 rounded-xl border border-border bg-card">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h1 className="text-2xl font-bold">{algorithm.name}</h1>
                        <p className="text-muted-foreground mt-1">{algorithm.description}</p>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <span className="px-2 py-1 rounded-md text-xs font-medium bg-primary/10 text-primary border border-primary/30">
                        {algorithm.strategy}
                      </span>
                      <span className="px-2 py-1 rounded-md text-xs font-medium bg-secondary text-secondary-foreground border border-border">
                        {algorithm.symbol}
                      </span>
                      <span className="px-2 py-1 rounded-md text-xs font-medium bg-secondary text-secondary-foreground border border-border">
                        {algorithm.timeframe}
                      </span>
                    </div>
                  </div>

                  {/* Parameters */}
                  <div className="p-6 rounded-xl border border-border bg-card">
                    <h2 className="text-lg font-semibold mb-4">Parameters</h2>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-muted-foreground">Position Size: </span>
                        <span className="font-medium">{algorithm.parameters.positionSize} lots</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Max Positions: </span>
                        <span className="font-medium">{algorithm.parameters.maxPositions}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Stop Loss: </span>
                        <span className="font-medium">{algorithm.parameters.stopLoss} pips</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Take Profit: </span>
                        <span className="font-medium">{algorithm.parameters.takeProfit} pips</span>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              <TradingControls
                isRunning={algorithm.status === 'active'}
                isPaused={false}
                onStart={handleStart}
                onPause={() => {}}
                onStop={handleStop}
                onEmergencyStop={handleEmergencyStop}
              />

              {!isEditing && (
                <Button onClick={() => setIsEditing(true)} className="w-full">
                  Edit Algorithm
                </Button>
              )}
            </div>
          </div>
        </TabPanel>

        <TabPanel value="performance" activeTab={activeTab}>
          <PerformanceDashboard algorithm={algorithm} />
        </TabPanel>

        <TabPanel value="settings" activeTab={activeTab}>
          <AlgorithmForm
            algorithm={algorithm}
            onSave={handleSave}
            onCancel={handleCancel}
          />
        </TabPanel>
      </TabsPanel>
    </div>
  )
}
